import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ConfirmationWindowComponent } from '@components/confirmation-window/confirmation-window.component';
import { RuntimeEnvironments } from '@environments/runtimeEnviroments.enum';
import { EnvironmentService } from '@service/environment.service';
import { OpsAlarmMonitoringService } from '@service/ops-alarms/ops-alarm-monitoring.service';
import { ResponsiveService } from '@service/responsive.service';
import { ThemeService } from '@service/theme.service';
import { VesselService } from '@service/vessel.service';
import { Observable, Subscription } from 'rxjs';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false,
})
export class AppComponent implements AfterViewInit {
    @ViewChild('confirmOverlay') confirmOverlay: ConfirmationWindowComponent;

    title = 'client';
    sidebarHidden: boolean = true;
    sidebarExtends: boolean = false;
    loadedIcons: Observable<any[]>;
    public env: RuntimeEnvironments;
    _isAnyAlarmUnacked: boolean = false;
    private newAlarmSubscription: Subscription;
    private updatedAlarmSubscription: Subscription;

    constructor(
        private environmentService: EnvironmentService,
        private iconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private themeService: ThemeService,
        private router: Router,
        public dialog: MatDialog,
        public responsive: ResponsiveService,
        private alarmMonitoringService: OpsAlarmMonitoringService,
        private vesselService: VesselService,
    ) {
        this.env = this.environmentService.getEnvironment();
        let pathIcons = '../assets/icons/';

        if (this.env === RuntimeEnvironments.ELECTRON) {
            pathIcons = 'assets/icons/';
        }

        const icons: [string, string][] = [
            ['blue-add', pathIcons + 'Blue-Add.svg'],
            ['blue-pencil', pathIcons + 'pencil-blue.svg'],
            ['search', pathIcons + 'input-search.svg'],
            ['download', pathIcons + 'download.svg'],
            ['fullscreen', pathIcons + 'fullscreen.svg'],
            ['screenshot', pathIcons + 'screenshot.svg'],
            ['alarm', pathIcons + 'bell.svg'],
            ['delete', pathIcons + 'Delete.svg'],
            ['value-pointer', pathIcons + 'value-pointer.svg'],
            ['error', pathIcons + 'error.svg'],
            ['cancel-close', pathIcons + 'Cancel-Close.svg'],
            ['expand-details', pathIcons + 'expand-details.svg'],
            ['arrow-left', pathIcons + 'arrow-left.svg'],
            ['chevron', pathIcons + 'chevron.svg'],
            ['preview', pathIcons + 'preview.svg'],
            ['refresh', pathIcons + 'refresh.svg'],
            ['info', pathIcons + 'info.svg'],
            ['lock', pathIcons + 'lock.svg'],
            ['lock-open', pathIcons + 'lock_open.svg'],
            ['lock-closed', pathIcons + 'lock_closed.svg'],
            ['edit-access', pathIcons + 'edit-access.svg'],
            ['view-access', pathIcons + 'view-access.svg'],
            ['icon-down', pathIcons + 'icon_down.svg'],
            ['password-hide', pathIcons + 'password-hide.svg'],
            ['password-watch', pathIcons + 'password-watch.svg'],
            ['user-profile', pathIcons + 'user-profile.svg'],
            ['system-status-menu-arrow', pathIcons + 'system-status-menu-arrow.svg'],
            ['status-indicator', pathIcons + 'status-indicator.svg'],
            ['no-alarms-icon', pathIcons + 'no-alarms-icon.svg'],
            ['system-status-disconnected', pathIcons + 'system-status-disconnected.svg'],
            ['location_on', pathIcons + 'location_on.svg'],
            ['status-error', pathIcons + 'status-error.svg'],
            ['status-check', pathIcons + 'status-check.svg'],
            ['monitor', pathIcons + 'monitor.svg'],
            ['more-info-status', pathIcons + 'more-info-status.svg'],
            ['dropdown-arrow', pathIcons + 'dropdown-arrow.svg'],

            //header
            ['leading', pathIcons + 'leading-icon.svg'],
            ['default-user', pathIcons + 'default-user-icon.svg'],
            ['status-alarm-good', pathIcons + 'status-alarm-good.svg'],
            ['status-alarm-bad', pathIcons + 'status-alarm-bad.svg'],
            ['status-maintenance-good', pathIcons + 'status-maintenance-good.svg'],
            ['status-maintenance-bad', pathIcons + 'status-maintenance-bad.svg'],

            //side
            ['dashboard', pathIcons + 'Dashboard.svg'],
            ['trendgraph', pathIcons + 'TrendGraphs.svg'],
            ['graphs', pathIcons + 'Graphs.svg'],
            ['mimics', pathIcons + 'Mimics.svg'],
            ['alarm-page', pathIcons + 'Alarms.svg'],
            ['channel-list', pathIcons + 'AllChannels.svg'],
            ['systemStatus', pathIcons + 'SystemStatus.svg'],
            ['setting', pathIcons + 'Settings.svg'],
            ['simulator', pathIcons + 'simulator.svg'],
            ['keyboard-double-arrow-right', pathIcons + 'keyboard-double-arrow-right.svg'],
            ['keyboard-double-arrow-left', pathIcons + 'keyboard-double-arrow-left.svg'],

            ['close', pathIcons + 'close.svg'],

            //custom graph
            ['plus', pathIcons + 'plus-symbol.svg'],
            ['minus', pathIcons + 'minus-symbol.svg'],
            ['fix-to-window', pathIcons + 'fix-to-window.svg'],
            ['open-as-tab', pathIcons + 'open-as-tab.svg'],

            ['check', pathIcons + 'check.svg'],
        ];

        for (let i = 0; i < icons.length; i++) {
            this.iconRegistry.addSvgIcon(
                icons[i][0],
                this.domSanitizer.bypassSecurityTrustResourceUrl(icons[i][1]),
            );
        }
    }

    ngOnInit() {
        this.updateAlarms();

        this.newAlarmSubscription = this.alarmMonitoringService.onNewAlarm().subscribe((v) => {
            this.updateAlarms();
        });
        this.updatedAlarmSubscription = this.alarmMonitoringService.onAlarmsUpdated$.subscribe(
            (v) => {
                this.updateAlarms();
            },
        );
    }

    async ngAfterViewInit() {
        try {
            this.themeService.setTheme();
            const vessel = await this.vesselService.getVessel();
            if (!vessel && RuntimeEnvironments.CLOUD) {
                if (this.router.url !== '/settings') {
                    const sub = this.confirmOverlay.onOptionSelected.subscribe(
                        (confirmed: boolean) => {
                            sub.unsubscribe();
                            if (confirmed) {
                                this.openProjectSettings();
                            }
                        },
                    );
                    this.confirmOverlay.setVisible(true);
                }
            } else if (this.router.url === '/') {
                this.router.navigate(['/ops/dashboard']);
            }
        } catch (err) {
            console.error(err);
        }
    }

    openProjectSettings() {
        this.router.navigate(['/settings']);
    }

    isSidebarHiddenScreenSize() {
        return (
            this.responsive.isMedium() || this.responsive.isSmall() || this.responsive.isXSmall()
        );
    }

    isSidebarHidden() {
        if (!this.isSidebarHiddenScreenSize()) {
            this.sidebarHidden = false;
        } else {
            this.sidebarHidden = true;
        }

        return this.isSidebarHiddenScreenSize() && this.sidebarHidden;
    }

    isSidebarExtneds() {
        return this.sidebarExtends;
    }

    onSidebarShow() {
        this.sidebarHidden = !this.sidebarHidden;
    }

    onSidebarExtends() {
        this.sidebarExtends = !this.sidebarExtends;
    }

    protected updateAlarms() {
        let alarmas = [...this.alarmMonitoringService.getAlarms()];
        this._isAnyAlarmUnacked = alarmas.some((v) => v.UnAck);
    }

    onStopBuzzerClick() {
        this.alarmMonitoringService.clickStopBuzzerData();
    }

    public get isAnyAlarmUnacked() {
        return this._isAnyAlarmUnacked;
    }
}
